/**
 * ============================================================================
 * WALLET SERVICE - API CLIENT
 * ============================================================================
 *
 * Handles all wallet-related API calls.
 * GET /wallets/me is cached and deduplicated to avoid 429 rate limits.
 */

import api, { handleApiError } from './api';

const WALLET_CACHE_MS = 30 * 1000; // 30 seconds
let walletCache = null;
let walletCacheTime = 0;
let walletInFlight = null;

/**
 * Get wallet information (cached 30s, concurrent calls share one request)
 */
export const getWallet = async () => {
  const now = Date.now();
  if (walletCache && now - walletCacheTime < WALLET_CACHE_MS) {
    return walletCache;
  }
  if (walletInFlight) {
    return walletInFlight;
  }
  try {
    walletInFlight = (async () => {
      const response = await api.get('/wallets/me');
      const data = response.data;
      walletCache = data;
      walletCacheTime = Date.now();
      return data;
    })();
    return await walletInFlight;
  } catch (error) {
    throw handleApiError(error);
  } finally {
    walletInFlight = null;
  }
};

/** Clear wallet cache (e.g. after logout or balance change) */
export const clearWalletCache = () => {
  walletCache = null;
  walletCacheTime = 0;
  walletInFlight = null;
};

/**
 * Get wallet balance
 */
export const getBalance = async () => {
  try {
    const response = await api.get('/wallets/me/balance');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request withdrawal
 */
export const requestWithdrawal = async (data) => {
  try {
    const response = await api.post('/wallets/withdrawals', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get withdrawal by ID
 */
export const getWithdrawal = async (withdrawalId) => {
  try {
    const response = await api.get(`/wallets/withdrawals/${withdrawalId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List withdrawals
 */
export const listWithdrawals = async (filters = {}) => {
  try {
    const response = await api.get('/wallets/withdrawals', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Cancel withdrawal
 */
export const cancelWithdrawal = async (withdrawalId) => {
  try {
    const response = await api.post(`/wallets/withdrawals/${withdrawalId}/cancel`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get wallet transactions
 */
export const getWalletTransactions = async (filters = {}) => {
  try {
    const response = await api.get('/wallets/transactions', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get wallet statistics
 */
export const getWalletStats = async () => {
  try {
    const response = await api.get('/wallets/me/stats');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get wallet by ID (admin only)
 */
export const getWalletById = async (walletId) => {
  try {
    const response = await api.get(`/wallets/${walletId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get wallet statistics
 */
export const getWalletStatistics = async () => {
  try {
    const response = await api.get('/wallets/me/statistics');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get wallet transaction by ID
 */
export const getWalletTransactionById = async (transactionId) => {
  try {
    const response = await api.get(`/wallets/transactions/${transactionId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Retry failed withdrawal
 */
export const retryWithdrawal = async (withdrawalId) => {
  try {
    const response = await api.post(`/wallets/withdrawals/${withdrawalId}/retry`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Estimate withdrawal fees
 */
export const estimateWithdrawalFees = async (data) => {
  try {
    const response = await api.post('/wallets/withdrawals/estimate-fees', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get withdrawal methods
 */
export const getWithdrawalMethods = async () => {
  try {
    const response = await api.get('/wallets/withdrawal-methods');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getWallet,
  clearWalletCache,
  getWalletById,
  getBalance,
  getWalletStatistics,
  requestWithdrawal,
  getWithdrawal,
  listWithdrawals,
  cancelWithdrawal,
  retryWithdrawal,
  getWalletTransactions,
  getWalletTransactionById,
  getWalletStats,
  estimateWithdrawalFees,
  getWithdrawalMethods,
};
